import { useEffect, useRef, useState } from "react";
import type { Journey, JourneyStepKey } from "./sessionJourney";

/** How long a step may sit unchanged before the page says it is slow. Generous: twice or more what the step normally
 * takes on dev (split ~1 min, on-device or result ~2 min, cloud labels ~5 min, render steps ~3 min). */
export function slowAfterMs(step: JourneyStepKey): number {
  switch (step) {
    case "split":
      return 3 * 60_000;
    case "cloud":
      return 15 * 60_000;
    case "script":
    case "voiceover":
    case "encode":
      return 10 * 60_000;
    default:
      return 5 * 60_000;
  }
}

/**
 * True once the journey's active step (and its frame count) has not changed for longer than that step's patience, so
 * the card can say so instead of spinning forever (dev 19979, 2026-10-14: stuck at "Splitting" for a day). Any change
 * of step or frame count restarts the clock; a finished journey is never slow.
 */
export function useJourneyStall(journey: Journey | null | undefined): boolean {
  const active = journey && !journey.finished ? journey.steps.find((s) => s.state === "active") : undefined;
  // The step's detail counts too: each voice-over line recorded is progress.
  const signature = active ? `${active.key}:${journey!.framesDone}:${active.detail ?? ""}` : null;
  const since = useRef<{ signature: string | null; at: number }>({ signature: null, at: Date.now() });
  if (since.current.signature !== signature) since.current = { signature, at: Date.now() };
  const [, tick] = useState(0);

  useEffect(() => {
    if (!signature) return;
    const t = window.setInterval(() => tick((n) => n + 1), 15_000);
    return () => window.clearInterval(t);
  }, [signature]);

  if (!active) return false;
  return Date.now() - since.current.at > slowAfterMs(active.key);
}
